import { useEffect, useState } from "react";
import { eng } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";
import { onNcrRaised, onInspectionSubmit, onBulkInspection } from "@/lib/events";
import { guardEdit } from "@/lib/perms";

// ============================================================
// Quality Control — faithful React port of vQC / vQCTable /
// vQCStation / vQCSite + their handlers. Reads all roll-ups from
// the engine; mutates through Store/setters and persists via
// saveToStorage (the original only re-rendered — we persist so
// edits reach Supabase).
// ============================================================

function useQc() {
  const version = useGrid((s) => s.version);
  const bump = useGrid((s) => s.bump);
  const setView = useGrid((s) => s.setView);
  void version;
  return { e: eng(), bump, setView };
}

// ---- shared lightbox (drives the static #lightbox infra in App) ----
let _lbList: any[] = [];
let _lbIdx = 0;
function lbShow(i: number) {
  const list = _lbList;
  if (!list.length) return;
  i = Math.max(0, Math.min(list.length - 1, i));
  _lbIdx = i;
  const p = list[i];
  const g = (id: string) => document.getElementById(id) as any;
  if (g("lbimg")) g("lbimg").src = p.full || p.thumb;
  if (g("lbcap")) g("lbcap").textContent = `${p.name || "photo"}${p.by ? ` · ${p.by}` : ""}`;
  if (g("lbcounter")) g("lbcounter").textContent = `${i + 1} / ${list.length}`;
  if (g("lbprev")) g("lbprev").disabled = i <= 0;
  if (g("lbnext")) g("lbnext").disabled = i >= list.length - 1;
}
function openLightbox(list: any[], id: string) {
  _lbList = list;
  const idx = list.findIndex((p) => p.id === id);
  if (idx < 0) return;
  const lb = document.getElementById("lightbox") as any;
  if (!lb) return;
  lb.style.display = "flex";
  const close = () => (lb.style.display = "none");
  (document.getElementById("lbclose") as any).onclick = close;
  lb.onclick = (e: any) => {
    if (e.target === lb) close();
  };
  (document.getElementById("lbprev") as any).onclick = () => lbShow(_lbIdx - 1);
  (document.getElementById("lbnext") as any).onclick = () => lbShow(_lbIdx + 1);
  lbShow(idx);
}

async function processAndAdd(files: FileList | File[], add: (out: any[]) => void, after: () => void) {
  const e = eng();
  const arr = [...files];
  if (!arr.length) return;
  const out: any[] = [];
  let failed = 0;
  for (const f of arr) {
    if (f.type.startsWith("image/")) {
      try {
        out.push(await e.processImage(f));
      } catch {
        failed++;
      }
    } else failed++;
  }
  if (out.length) {
    add(out);
    e.toast(`${out.length} photo${out.length > 1 ? "s" : ""} added`, "success");
    e.checkStorageHealth();
  }
  if (failed) e.toast(`${failed} photo${failed > 1 ? "s" : ""} could not be processed`, "warn");
  after();
}

// ---- shared presentational bits ----
function FilterPills({ filt, counts, onSet }: { filt: string; counts: any; onSet: (k: string) => void }) {
  return (
    <div className="cl-filter">
      <button className={`cl-pill ${filt === "all" ? "sel" : ""}`} onClick={() => onSet("all")}>
        All <b>{counts.total}</b>
      </button>
      <button className={`cl-pill ${filt === "pass" ? "sel" : ""} pass`} onClick={() => onSet("pass")}>
        Pass <b>{counts.pass}</b>
      </button>
      <button className={`cl-pill ${filt === "fail" ? "sel" : ""} fail`} onClick={() => onSet("fail")}>
        Fail <b>{counts.fail}</b>
      </button>
      <button className={`cl-pill ${filt === "pending" ? "sel" : ""} pending`} onClick={() => onSet("pending")}>
        Open <b>{counts.pending}</b>
      </button>
    </div>
  );
}

function Gallery({ photos, scopeTag, onAdd, onDel, onView }: { photos: any[]; scopeTag?: string; onAdd: (files: FileList) => void; onDel: (id: string) => void; onView: (id: string) => void }) {
  return (
    <>
      <div className="field-l">
        Photo evidence {photos.length ? `· ${photos.length}` : ""}
        {scopeTag}
      </div>
      <div className="gallery">
        {photos.map((p) => (
          <div className="thumb" key={p.id} onClick={() => onView(p.id)} style={{ cursor: "pointer" }}>
            <img src={p.thumb} alt={p.name || "photo"} />
            <button
              className="thdel"
              title="Remove"
              onClick={(ev) => {
                ev.stopPropagation();
                onDel(p.id);
              }}
            >
              ✕
            </button>
          </div>
        ))}
        <label className="addphoto">
          <span className="pi-cam">+</span>
          <span>Add photo</span>
          <input type="file" accept="image/*" capture="environment" multiple style={{ display: "none" }} onChange={(ev) => ev.target.files && onAdd(ev.target.files)} />
        </label>
      </div>
    </>
  );
}

function Thread({ comments, scopeTag, onPost, onDel }: { comments: any[]; scopeTag?: string; onPost: (text: string) => void; onDel: (id: string) => void }) {
  const e = eng();
  const [txt, setTxt] = useState("");
  return (
    <>
      <div className="field-l">
        Comments {comments.length ? `· ${comments.length}` : ""}
        {scopeTag}
      </div>
      <div className="thread">
        {comments.length ? (
          comments.map((c) => (
            <div className="cmt" key={c.id}>
              <div className="cmt-h">
                <span className="cmt-by">{c.by}</span>
                <span className="cmt-at">
                  {e.fmtDate(c.at)} · {e.fmtTime(c.at)}
                </span>
                <button className="cmt-del" title="Delete" onClick={() => onDel(c.id)}>
                  ✕
                </button>
              </div>
              <div className="cmt-tx">{c.text}</div>
            </div>
          ))
        ) : (
          <div className="cmt-empty">No comments yet. Add an observation, deviation, or rework note.</div>
        )}
      </div>
      <div className="cmt-compose">
        <textarea className="note" rows={2} placeholder="Write a comment…" maxLength={500} value={txt} onChange={(ev) => setTxt(ev.target.value)} />
        <button
          className="cmt-send"
          disabled={!txt.trim()}
          onClick={() => {
            if (txt.trim()) {
              onPost(txt.trim());
              setTxt("");
            }
          }}
        >
          Post
        </button>
      </div>
    </>
  );
}

function overallOf(e: any, overall: string): [string, string] {
  return overall === "pass" ? ["ACCEPTED", e.C.grass] : overall === "fail" ? ["REJECTED", e.C.rust] : overall === "partial" ? ["IN PROGRESS", e.C.amber] : ["NOT STARTED", e.C.dim2];
}

function IPanelShell({ children }: { children: React.ReactNode }) {
  const bump = useGrid((s) => s.bump);
  const e = eng();
  const collapse = () => { e.state.qcPanelCollapsed = true; bump(); };
  return (
    <>
      {/* #10 (tablet): scrim behind the slide-over panel. CSS only shows it on
          tablet widths while the panel is open; tapping it closes the panel. */}
      <div className="qc-backdrop" onClick={collapse} aria-hidden="true" />
      <div className="ipanel">
        <button
          className="ipanel-toggle"
          title="Hide details panel"
          onClick={collapse}
        >
          ›
        </button>
        {children}
      </div>
      <button
        className="ipanel-floating"
        title="Show details panel"
        onClick={() => {
          e.state.qcPanelCollapsed = false;
          bump();
        }}
      >
        ‹
      </button>
    </>
  );
}

// ============================================================
// Issue #12 — four work sections (per the client screenshot). Foundations and
// Mechanical both inspect the same physical tables (so they share TableScope and
// the engine's "table" work scope), differing only in the default sub-view they
// open: Foundations focuses on the N/S piles, Mechanical on the modules.
// Electrical = the transformer stations/inverters, Civil & Infrastructure = the
// site-wide ITP. `qcSection` is a React-only UI field on the engine state object;
// the engine itself still keys everything off qcWorkScope ∈ table/station/site.
type Section = "foundations" | "mechanical" | "electrical" | "civil";
const SECTION_WS: Record<Section, string> = { foundations: "table", mechanical: "table", electrical: "station", civil: "site" };

export default function QcView() {
  const { e, bump } = useQc();
  const ws = e.state.qcWorkScope || "table";
  // Honour qcSection only when it still matches the engine work scope (a punch /
  // RFI deep-link can set qcWorkScope directly without touching qcSection).
  const rawSec = e.state.qcSection as Section | undefined;
  const section: Section = rawSec && SECTION_WS[rawSec] === ws ? rawSec : ws === "station" ? "electrical" : ws === "site" ? "civil" : "mechanical";

  // #10 (tablet): the right-hand inspection panel becomes a slide-over below
  // 1280px. Default it CLOSED there on first open so the middle column's
  // Modules / Piles / Accept / Reject / Reset buttons are visible (the client
  // couldn't see them because the panel opened over them). Desktop keeps it open.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    if (window.matchMedia("(max-width: 1280px)").matches && !e.state.qcPanelCollapsed) {
      e.state.qcPanelCollapsed = true;
      bump();
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSection = (sec: Section) => {
    const newWs = SECTION_WS[sec];
    e.state.qcSection = sec;
    e.state.qcWorkScope = newWs;
    e.state.qcItem = 0;
    if (newWs !== "table") {
      e.state.qcModule = null;
      e.state.qcPile = null;
      e.state.showModules = false;
      e.state.showPiles = false;
    } else if (sec === "foundations") {
      e.state.showPiles = true; e.state.showModules = false; e.state.qcModule = null;
    } else {
      // mechanical → open with the module grid showing
      e.state.showModules = true; e.state.showPiles = false; e.state.qcPile = null;
    }
    e.state.mobileStep = newWs === "site" ? "checklist" : "nav";
    bump();
  };

  // Count only records with REAL inspection work — opening a row/inverter lazily
  // creates an empty pending record, which must not count as progress. (client issue)
  const tablesInspected = Object.keys(e.inspections).filter((k) => !k.includes("::M") && !k.includes("::P") && e.recStarted(e.inspections[k])).length;
  let stationInvDone = 0;
  e.D.stations.forEach((st: any) => {
    for (let i = 0; i < st.invCount; i++) if (e.recStarted(e.getStationInv(st.id, i))) stationInvDone++;
  });
  const totalInv = e.D.stations.reduce((a: number, s: any) => a + s.invCount, 0);
  const siteDone = Object.values(e.siteInspection.items || {}).filter((r: any) => e.itemStarted(r)).length;

  const Pill = ({ sec, label, count }: { sec: Section; label: string; count: string }) => (
    <button className={`ws-pill ${section === sec ? "sel" : ""}`} onClick={() => setSection(sec)}>
      <span className="ws-l">{label}</span>
      <span className="ws-c">{count}</span>
    </button>
  );

  return (
    <div className="qc-wrap-outer">
      <div className="ws-bar">
        <Pill sec="foundations" label="Foundations" count="N/S piles" />
        <Pill sec="mechanical" label="Mechanical" count={`${tablesInspected} in progress`} />
        <Pill sec="electrical" label="Electrical" count={`${stationInvDone}/${totalInv} inverters`} />
        <Pill sec="civil" label="Civil & Infrastructure" count={`${siteDone}/${e.D.siteItp.length} items`} />
      </div>
      {section === "electrical" ? <StationScope /> : section === "civil" ? <SiteScope /> : <TableScope />}
    </div>
  );
}

// ---------------- SITE SCOPE ----------------
function SiteScope() {
  const { e, bump } = useQc();
  const ITP = e.D.siteItp;
  const sr = e.siteRoll();
  if (e.state.qcItem >= ITP.length) e.state.qcItem = 0;
  const counts = { total: ITP.length, pass: sr.pass, fail: sr.fail, pending: sr.total - sr.done };
  const getRes = (i: number) => e.siteItemResult(i);

  const setItem = (i: number, s: string) => {
    if (!guardEdit()) return;
    e.pushHistory();
    const before = e.ncrSeq;
    e.Store.setSiteItem(i, s);
    if (s === "fail" && e.ncrSeq > before) {
      const ref = e.ncrRef(e.ncrSeq - 1);
      e.toast(ref + " raised", "warn");
      onNcrRaised(ref, "Site-wide");
    }
    onInspectionSubmit("Site-wide", i, s);
    e.state.qcItem = i;
    e.saveToStorage();
    bump();
  };

  const it = ITP[e.state.qcItem];
  const res = getRes(e.state.qcItem);
  const ov = overallOf(e, sr.overall);

  return (
    <div className={`qcgrid ${e.state.qcPanelCollapsed ? "ipanel-collapsed" : ""}`}>
      <div className="sidebar">
        <div className="sbhead">
          <span>Site-wide work</span>
          <span className="mono">{ITP.length} items</span>
        </div>
        <div style={{ padding: "14px 16px", color: "var(--dim)", fontSize: 11.5, lineHeight: 1.6 }}>
          Civil and infrastructure work that lives at <b style={{ color: "var(--ink)" }}>project level</b>, not per-table. Roads, fences, crossings, drainage, compound and substation boundary apply to the whole site.
        </div>
      </div>
      <div className="insp-mid">
        <div className="qcbar">
          <div className="crumb">
            <span className="cb hl">Site-wide civil &amp; infrastructure</span>
          </div>
          <div className="tableoverall" style={{ ["--oc" as any]: ov[1] }}>
            <span className="ostat">{ov[0]}</span>
            <span className="oprog">
              {sr.done}/{sr.total} items · {sr.pass}✓ {sr.fail}✕
            </span>
          </div>
        </div>
        <div className="checklist">
          <FilterPills filt={e.state.qcChecklistFilter || "all"} counts={counts} onSet={(k) => { e.state.qcChecklistFilter = k; bump(); }} />
          <FilteredChecklist itp={ITP} filt={e.state.qcChecklistFilter || "all"} getRes={getRes} qcItem={e.state.qcItem} onPick={(i: number) => { e.state.qcItem = i; bump(); }} onSet={setItem} e={e} />
        </div>
      </div>
      <IPanelShell>
        <div className="lbl">
          {it.code}-{String(e.state.qcItem + 1).padStart(3, "0")} · Site-wide
        </div>
        <div className="iname">{it.item}</div>
        <div className="field-l">Acceptance criteria</div>
        <div className="crit" style={{ whiteSpace: "pre-wrap" }}>
          {it.crit || "—"}
        </div>
        <ResultRow res={res} onSet={(s) => setItem(e.state.qcItem, s)} e={e} />
        <Gallery
          photos={res.photos || []}
          onAdd={(files) => processAndAdd(files, (out) => { e.pushHistory(); e.Store.addSitePhotos(e.state.qcItem, out); e.saveToStorage(); }, bump)}
          onDel={(id) => { if (confirm("Remove this photo?")) { e.pushHistory(); e.Store.deleteSitePhoto(e.state.qcItem, id); e.saveToStorage(); bump(); } }}
          onView={(id) => openLightbox(e.siteItemResult(e.state.qcItem).photos || [], id)}
        />
        <Thread comments={res.comments || []} onPost={(t) => { e.pushHistory(); e.Store.addSiteComment(e.state.qcItem, t); e.saveToStorage(); bump(); }} onDel={(id) => { e.pushHistory(); e.Store.deleteSiteComment(e.state.qcItem, id); e.saveToStorage(); bump(); }} />
        <button className="export" onClick={() => e.exportSiteReport()}>
          Export site-wide acceptance report
        </button>
      </IPanelShell>
    </div>
  );
}

// ---------------- STATION SCOPE ----------------
function StationScope() {
  const { e, bump } = useQc();
  const sid = e.state.qcStation || "ST1";
  const station = e.D.stations.find((s: any) => s.id === sid) || e.D.stations[0];
  const invIdx = e.state.qcInverter | 0;
  // Real CAD designator (e.g. "TR3-INV5") so QC numbering matches the Site Map.
  const invRef = e.invLabel(station.id, invIdx);
  const ITP = e.D.stationItp;
  const ir = e.stationInvRoll(station.id, invIdx);
  if (e.state.qcItem >= ITP.length) e.state.qcItem = 0;
  const counts = { total: ITP.length, pass: ir.pass, fail: ir.fail, pending: ir.total - ir.done };
  const getRes = (i: number) => e.stationInvItemResult(station.id, invIdx, i);

  const setItem = (i: number, s: string) => {
    if (!guardEdit()) return;
    e.pushHistory();
    const before = e.ncrSeq;
    e.Store.setStationInvItem(station.id, invIdx, i, s);
    if (s === "fail" && e.ncrSeq > before) {
      const ref = e.ncrRef(e.ncrSeq - 1);
      e.toast(ref + " raised", "warn");
      onNcrRaised(ref, `${invRef}`);
    }
    onInspectionSubmit(`${invRef}`, i, s);
    e.state.qcItem = i;
    e.saveToStorage();
    bump();
  };
  const bulk = (v: string) => {
    if (!guardEdit()) return;
    if (v === "clear" && !confirm(`Reset all items on ${invRef}?`)) return;
    if (v === "fail" && !confirm(`Mark every item on ${invRef} as FAILED? This will raise NCRs.`)) return;
    e.pushHistory();
    ITP.forEach((_: any, i: number) => e.Store.setStationInvItem(station.id, invIdx, i, v));
    e.saveToStorage();
    onBulkInspection(`${invRef}`, v);
    bump();
    e.toast(v === "pass" ? `${invRef} accepted` : v === "fail" ? `${invRef} rejected` : `${invRef} reset`, v === "pass" ? "success" : v === "fail" ? "warn" : "info");
  };

  const it = ITP[e.state.qcItem];
  const res = getRes(e.state.qcItem);
  const ov = overallOf(e, ir.overall);

  return (
    <div className={`qcgrid ${e.state.qcPanelCollapsed ? "ipanel-collapsed" : ""}`}>
      <div className="sidebar">
        <div className="sbhead">
          <span>Stations</span>
          <span className="mono">8 · {e.D.stations.reduce((a: number, s: any) => a + s.invCount, 0)} inverters</span>
        </div>
        {e.D.stations.map((st: any) => {
          const open = st.id === station.id;
          const r = e.stationRoll(st);
          const dot = r.fail > 0 ? e.C.rust : r.pass > 0 ? e.C.grass : e.C.dim2;
          return (
            <div className="catgroup" key={st.id}>
              <div className={`cathead navfield ${open ? "open" : ""}`} onClick={() => { e.state.qcStation = st.id; e.state.qcInverter = 0; e.state.qcItem = 0; bump(); }} style={{ cursor: "pointer" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span className="sq" style={{ background: dot }} />
                  {st.label}
                </span>
                <span className="cc">
                  {st.invCount} inv · {r.done}/{r.total}
                </span>
              </div>
              {open ? (
                <div className="rowlist" style={{ padding: "8px 12px" }}>
                  <div className="invgrid">
                    {Array.from({ length: st.invCount }).map((_, i) => {
                      const rr = e.stationInvRoll(st.id, i);
                      const bg = rr.overall === "pass" ? e.C.grass : rr.overall === "fail" ? e.C.rust : rr.overall === "partial" ? e.C.amber : "var(--panel2)";
                      const col = rr.overall === "pass" || rr.overall === "fail" || rr.overall === "partial" ? e.C.bg : "var(--dim)";
                      return (
                        <button key={i} className={`invcell ${i === invIdx ? "sel" : ""}`} onClick={() => { e.state.qcInverter = i; e.state.qcItem = 0; bump(); }} style={{ background: bg, color: col }} title={`${e.invLabel(st.id, i)} · ${rr.overall}`}>
                          {e.invShort(st.id, i)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="insp-mid">
        <div className="qcbar">
          <div className="crumb">
            <span className="cb">{station.label}</span>
            <span className="cs">/</span>
            <span className="cb hl">{e.invShort(station.id, invIdx)}</span>
          </div>
          <div className="tableoverall" style={{ ["--oc" as any]: ov[1] }}>
            <span className="ostat">{ov[0]}</span>
            <span className="oprog">
              {ir.done}/{ir.total} items · {ir.pass}✓ {ir.fail}✕
            </span>
            <div className="oactions">
              <button className="obtn ok" onClick={() => bulk("pass")}>
                Accept inverter
              </button>
              <button className="obtn no" onClick={() => bulk("fail")}>
                Reject
              </button>
              <button className="obtn nu" onClick={() => bulk("clear")}>
                Reset
              </button>
            </div>
          </div>
        </div>
        <div className="checklist">
          <FilterPills filt={e.state.qcChecklistFilter || "all"} counts={counts} onSet={(k) => { e.state.qcChecklistFilter = k; bump(); }} />
          <FilteredChecklist itp={ITP} filt={e.state.qcChecklistFilter || "all"} getRes={getRes} qcItem={e.state.qcItem} onPick={(i: number) => { e.state.qcItem = i; bump(); }} onSet={setItem} e={e} />
        </div>
      </div>
      <IPanelShell>
        <div className="lbl">
          {it.code}-{String(e.state.qcItem + 1).padStart(3, "0")} · {invRef}
        </div>
        <div className="iname">{it.item}</div>
        <div className="field-l">Acceptance criteria</div>
        <div className="crit" style={{ whiteSpace: "pre-wrap" }}>
          {it.crit || "—"}
        </div>
        <ResultRow res={res} onSet={(s) => setItem(e.state.qcItem, s)} e={e} />
        <Gallery
          photos={res.photos || []}
          onAdd={(files) => processAndAdd(files, (out) => { e.pushHistory(); e.Store.addStationInvPhotos(station.id, invIdx, e.state.qcItem, out); e.saveToStorage(); }, bump)}
          onDel={(id) => { if (confirm("Remove this photo?")) { e.pushHistory(); e.Store.deleteStationInvPhoto(station.id, invIdx, e.state.qcItem, id); e.saveToStorage(); bump(); } }}
          onView={(id) => openLightbox(getRes(e.state.qcItem).photos || [], id)}
        />
        <Thread comments={res.comments || []} onPost={(t) => { e.pushHistory(); e.Store.addStationInvComment(station.id, invIdx, e.state.qcItem, t); e.saveToStorage(); bump(); }} onDel={(id) => { e.pushHistory(); e.Store.deleteStationInvComment(station.id, invIdx, e.state.qcItem, id); e.saveToStorage(); bump(); }} />
        <button className="export" onClick={() => e.exportStationReport()}>
          Export inverter report — {invRef}
        </button>
      </IPanelShell>
    </div>
  );
}

// ---------------- TABLE SCOPE ----------------
function TableScope() {
  const { e, bump } = useQc();
  const C = e.C;
  const D = e.D;
  const f = D.fields.find((x: any) => x.id === e.state.qcField) || D.fields[0];
  const nRows = e.rowsForField(f);
  const rowTables = e.tablesInRow(f, e.state.qcRow);
  if (!rowTables.includes(e.state.qcTable)) e.state.qcTable = rowTables[0];
  const fid = f.id;
  const t = e.state.qcTable;
  const roll = e.tableRoll(fid, t);
  const tt = D.tableTypes[e.tableTypeFor(f, t)];
  const inMod = e.state.qcModule != null;
  const inPile = e.state.qcPile != null;
  const M = e.state.qcModule;
  const P = e.state.qcPile;
  const ITP = inPile ? D.pileItp : D.tableItp;
  if (e.state.qcItem >= ITP.length) e.state.qcItem = 0;

  const getRes = (i: number) => (inPile ? e.pileItemResult(fid, t, P, i) : inMod ? e.modItemResult(fid, t, M, i) : e.itemResult(fid, t, i));
  let pass = 0;
  let fail = 0;
  let pending = 0;
  ITP.forEach((_: any, i: number) => {
    const s = getRes(i).status;
    if (s === "pass") pass++;
    else if (s === "fail") fail++;
    else pending++;
  });
  const counts = { total: ITP.length, pass, fail, pending };

  const setItem = (i: number, s: string) => {
    if (!guardEdit()) return;
    e.pushHistory();
    const before = e.ncrSeq;
    if (inPile) e.Store.setPileItem(fid, t, P, i, s);
    else if (inMod) e.Store.setModuleItem(fid, t, M, i, s);
    else e.setItemStatus(fid, t, i, s);
    if (s === "fail" && e.ncrSeq > before) {
      const ref = e.ncrRef(e.ncrSeq - 1);
      e.toast(ref + " raised", "warn");
      onNcrRaised(ref, `${fid} · T${t}`);
    }
    onInspectionSubmit(`${fid} · T${t}`, i, s);
    e.state.qcItem = i;
    e.saveToStorage();
    bump();
  };

  const tableAll = (action: string) => {
    if (!guardEdit()) return;
    if (action === "clear" && !confirm(`Reset Table ${t} entirely? This clears the checklist and all module + pile statuses.`)) return;
    if (action === "fail" && !confirm(`Mark Table ${t} as FAILED? This raises NCRs and marks all modules and piles failed.`)) return;
    e.pushHistory();
    e.setTableAll(fid, t, action);
    e.saveToStorage();
    onBulkInspection(`${fid} · T${t}`, action);
    bump();
    e.toast(action === "pass" ? `Table ${t} accepted` : action === "fail" ? `Table ${t} rejected` : `Table ${t} reset`, action === "pass" ? "success" : action === "fail" ? "warn" : "info");
  };
  const modAll = (v: string) => {
    if (M == null) return;
    e.pushHistory();
    if (v === "clear") e.Store.setModuleStatus(fid, t, M, "clear");
    else {
      e.Store.setModuleStatus(fid, t, M, v);
      D.itp.forEach((_: any, i: number) => e.Store.setModuleItem(fid, t, M, i, v));
    }
    e.saveToStorage();
    bump();
  };
  const pileAll = (v: string) => {
    if (P == null) return;
    e.pushHistory();
    if (v === "clear") e.Store.setPileStatus(fid, t, P, "clear");
    else {
      e.Store.setPileStatus(fid, t, P, v);
      D.pileItp.forEach((_: any, i: number) => e.Store.setPileItem(fid, t, P, i, v));
    }
    e.saveToStorage();
    bump();
  };

  const ov = overallOf(e, roll.overall);
  const it = ITP[e.state.qcItem];
  const res = getRes(e.state.qcItem);
  const evid = inPile ? e.getPile(fid, t, P) : inMod ? e.getModule(fid, t, M) : res;
  const cols = tt.cols;
  const rows = tt.rows;
  const perRow = tt.pilesPerRow;
  const mr = e.moduleTableRoll(fid, t, cols);
  const pr = e.pileTableRoll(fid, t, perRow);
  const showModules = e.state.showModules || inMod;
  const showPiles = e.state.showPiles || inPile;
  const scopeTag = inPile ? " · pile" : inMod ? " · module" : "";
  const modRow = inMod ? Math.floor(M / cols) + 1 : 0;
  const modCol = inMod ? (M % cols) + 1 : 0;
  const pInfo = inPile ? e.pileInfo(P, perRow) : null;

  return (
    <div className={`qcgrid ${e.state.qcPanelCollapsed ? "ipanel-collapsed" : ""}`}>
      <div className="sidebar">
        <div className="sbhead">
          <span>Inspection navigator</span>
          <span className="mono">ITP-BAL</span>
        </div>
        {D.fields.map((fl: any) => {
          const open = fl.id === e.state.qcField;
          let acc = 0;
          let flFail = 0;
          let insp = 0;
          for (let tt2 = 1; tt2 <= fl.tables; tt2++) {
            const rr = e.tableRoll(fl.id, tt2);
            if (rr.overall === "pass") acc++;
            else if (rr.overall === "fail") flFail++;
            if (rr.done > 0) insp++;
          }
          const dot = flFail > 0 ? C.rust : acc > 0 ? C.grass : C.dim2;
          return (
            <div className="catgroup" key={fl.id}>
              <div
                className={`cathead navfield ${open ? "open" : ""}`}
                onClick={() => {
                  if (e.state.qcField !== fl.id) {
                    e.state.qcField = fl.id;
                    e.state.qcRow = 1;
                    e.state.qcTable = 1;
                    e.state.qcItem = 0;
                    e.state.qcModule = null;
                    e.state.showModules = false;
                    e.state.qcPile = null;
                    e.state.showPiles = false;
                  }
                  bump();
                }}
                style={{ cursor: "pointer" }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="sq" style={{ background: dot, borderRadius: "50%" }} />
                  {fl.id}
                </span>
                <span className="cc">
                  {fl.tables} tbl{insp ? ` · ${acc}✓` : ""}
                </span>
              </div>
              {open ? (
                <div className="rowlist">
                  {Array.from({ length: nRows }).map((_, ri) => {
                    const row = ri + 1;
                    const tabs = e.tablesInRow(fl, row);
                    let rPass = 0;
                    let rFail = 0;
                    let rDone = 0;
                    tabs.forEach((tt2: number) => {
                      const rr = e.tableRoll(fl.id, tt2);
                      if (rr.overall === "pass") rPass++;
                      else if (rr.overall === "fail") rFail++;
                      if (rr.done > 0) rDone++;
                    });
                    const rc = rFail > 0 ? C.rust : rPass > 0 ? C.grass : C.dim2;
                    return (
                      <div
                        className={`rowitem ${row === e.state.qcRow ? "sel" : ""}`}
                        key={row}
                        onClick={() => {
                          e.state.qcRow = row;
                          e.state.qcTable = e.tablesInRow(D.fields.find((x: any) => x.id === e.state.qcField), row)[0];
                          e.state.qcItem = 0;
                          e.state.qcModule = null;
                          e.state.showModules = false;
                          e.state.qcPile = null;
                          e.state.showPiles = false;
                          bump();
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <span className="rlab">Row {String(row).padStart(2, "0")}</span>
                        <span className="rmeta">
                          <span className="sq" style={{ background: rc }} />
                          {tabs.length} tables{rDone ? ` · ${rDone} insp.` : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="insp-mid">
        <div className="qcbar">
          <div className="crumb">
            <span className="cb">{fid}</span>
            <span className="cs">/</span>
            <span className="cb">Row {String(e.state.qcRow).padStart(2, "0")}</span>
            <span className="cs">/</span>
            <span className="cb hl">Table {t}</span>
          </div>
          <div className="tableoverall" style={{ ["--oc" as any]: ov[1] }}>
            <span className="ostat">{ov[0]}</span>
            <span className="oprog">
              {roll.done}/{roll.total} items · {roll.pass}✓ {roll.fail}✕
            </span>
            <div className="oactions">
              <button className={`obtn mod-toggle ${showModules ? "on" : ""}`} onClick={() => { e.state.showModules = !e.state.showModules; if (e.state.showModules) { e.state.showPiles = false; e.state.qcPile = null; } else e.state.qcModule = null; e.state.qcItem = 0; bump(); }}>
                [G] Modules
              </button>
              <button className={`obtn pile-toggle ${showPiles ? "on" : ""}`} onClick={() => { e.state.showPiles = !e.state.showPiles; if (e.state.showPiles) { e.state.showModules = false; e.state.qcModule = null; } else e.state.qcPile = null; e.state.qcItem = 0; bump(); }}>
                [P] Piles
              </button>
              <button className="obtn ok" onClick={() => tableAll("pass")}>
                Accept table
              </button>
              <button className="obtn no" onClick={() => tableAll("fail")}>
                Reject
              </button>
              <button className="obtn nu" onClick={() => tableAll("clear")}>
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="rowstrip">
          {rowTables.map((tt2: number) => {
            const rr = e.tableRoll(fid, tt2);
            const col = rr.overall === "pass" ? C.grass : rr.overall === "fail" ? C.rust : rr.overall === "partial" ? C.amber : "transparent";
            const fg = rr.overall === "pass" || rr.overall === "fail" ? C.bg : C.ink;
            const ty = D.tableTypes[e.tableTypeFor(f, tt2)];
            return (
              <button
                key={tt2}
                className={`tabchip ${tt2 === t ? "sel" : ""}`}
                onClick={() => { e.state.qcTable = tt2; e.state.qcItem = 0; e.state.qcModule = null; e.state.showModules = false; e.state.qcPile = null; e.state.showPiles = false; bump(); }}
                title={`${ty.label} · ${rr.done}/${rr.total} items`}
                style={{ background: col, color: fg, borderColor: col === "transparent" ? ty.color : col }}
              >
                <b>T{tt2}</b>
                <span>
                  {rr.done}/{rr.total}
                </span>
              </button>
            );
          })}
        </div>

        {showModules ? (
          <div className="modwrap">
            <div className="modhdr">
              <div className="modttl">
                Table {t} · <span className="mono">{tt.label}</span> — module layout
              </div>
              <div className="modsum">
                <span style={{ color: C.grass }}>{mr.acc}✓</span>
                <span style={{ color: mr.fail ? C.rust : C.dim }}>{mr.fail}✕</span>
                <span style={{ color: C.dim }}>
                  {mr.done}/{mr.n} modules
                </span>
              </div>
            </div>
            <div className="modgrid">
              {Array.from({ length: rows }).map((_, row) => (
                <div className="modrow" key={row}>
                  {Array.from({ length: cols }).map((_, cIdx) => {
                    const m = row * cols + cIdx;
                    const rr = e.moduleRoll(fid, t, m);
                    const col = rr.overall === "pass" ? C.grass : rr.overall === "fail" ? C.rust : rr.overall === "partial" ? C.amber : "transparent";
                    return <button key={m} className={`mod ${e.state.qcModule === m ? "sel" : ""}`} onClick={() => { e.state.qcModule = m; e.state.qcItem = 0; bump(); }} title={`Module R${row + 1}·C${cIdx + 1} — ${rr.overall}`} style={{ background: col === "transparent" ? "var(--bg2)" : col, borderColor: col === "transparent" ? "var(--line2)" : col }} />;
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {inMod ? (
          <div className="modscope">
            <span className="modscope-l">
              Inspecting <b>Module R{modRow}·C{modCol}</b> of Table {t}
            </span>
            <div className="oactions">
              <button className="obtn ok" onClick={() => modAll("pass")}>Accept module</button>
              <button className="obtn no" onClick={() => modAll("fail")}>Fail module</button>
              <button className="obtn nu" onClick={() => modAll("clear")}>Reset</button>
              <button className="obtn" onClick={() => { e.state.qcModule = null; bump(); }}>← Whole table</button>
            </div>
          </div>
        ) : null}

        {showPiles ? (
          <div className="modwrap pilewrap">
            <div className="modhdr">
              <div className="modttl">
                Table {t} · <span className="mono">{tt.label}</span> — pile layout (N + S rows · {perRow} per row)
              </div>
              <div className="modsum">
                <span style={{ color: C.grass }}>{pr.acc}✓</span>
                <span style={{ color: pr.fail ? C.rust : C.dim }}>{pr.fail}✕</span>
                <span style={{ color: C.dim }}>
                  {pr.done}/{pr.n} piles
                </span>
              </div>
            </div>
            <div className="pilegrid">
              {(["N", "S"] as const).map((side) => (
                <div className="pilerow" key={side}>
                  <span className="pilelab">{side === "N" ? "North" : "South"}</span>
                  <div className="pilecells">
                    {Array.from({ length: perRow }).map((_, c) => {
                      const p = (side === "N" ? 0 : perRow) + c;
                      const rr = e.pileRoll(fid, t, p);
                      const col = rr.overall === "pass" ? C.grass : rr.overall === "fail" ? C.rust : rr.overall === "partial" ? C.amber : "transparent";
                      return <button key={p} className={`pile ${e.state.qcPile === p ? "sel" : ""}`} onClick={() => { e.state.qcPile = p; e.state.qcItem = 0; bump(); }} title={`${side} pile ${c + 1} — ${rr.overall}`} style={{ background: col === "transparent" ? "var(--bg2)" : col, borderColor: col === "transparent" ? "var(--line2)" : col }}>{side}{c + 1}</button>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {inPile ? (
          <div className="modscope">
            <span className="modscope-l">
              Inspecting <b>{pInfo!.side === "N" ? "North" : "South"} pile {pInfo!.col}</b> of Table {t}
            </span>
            <div className="oactions">
              <button className="obtn ok" onClick={() => pileAll("pass")}>Accept pile</button>
              <button className="obtn no" onClick={() => pileAll("fail")}>Fail pile</button>
              <button className="obtn nu" onClick={() => pileAll("clear")}>Reset</button>
              <button className="obtn" onClick={() => { e.state.qcPile = null; bump(); }}>← Whole table</button>
            </div>
          </div>
        ) : null}

        <div className="checklist">
          <FilterPills filt={e.state.qcChecklistFilter || "all"} counts={counts} onSet={(k) => { e.state.qcChecklistFilter = k; bump(); }} />
          <FilteredChecklist itp={ITP} filt={e.state.qcChecklistFilter || "all"} getRes={getRes} qcItem={e.state.qcItem} onPick={(i: number) => { e.state.qcItem = i; bump(); }} onSet={setItem} e={e} />
        </div>
      </div>

      <IPanelShell>
        <div className="lbl">
          Inspection item {String(e.state.qcItem + 1).padStart(3, "0")} · BAL-{it.code}-{String(e.state.qcItem + 1).padStart(3, "0")}
          {scopeTag}
        </div>
        <div className="iname serif">{it.item}</div>
        <div className="icat">
          {it.cat}
          {inPile ? ` · ${pInfo!.side === "N" ? "North" : "South"} pile ${pInfo!.col}` : inMod ? ` · Module R${modRow}·C${modCol}` : ""}
        </div>
        <div className="metagrid">
          <div className="mcell">
            <b>Location</b>
            <span className="mono">
              {inPile ? `${fid} · T${t} · ${pInfo!.side}${pInfo!.col}` : inMod ? `${fid} · T${t} · Module R${modRow}·C${modCol}` : `${fid} · R${String(e.state.qcRow).padStart(2, "0")} · T${t}`}
            </span>
          </div>
          <div className="mcell">
            <b>Table type</b>
            <span className="mono">{tt.label}</span>
          </div>
          <div className="mcell">
            <b>NCR linked</b>
            <span className="mono" style={{ color: res.ncr ? C.rust : C.dim }}>
              {res.ncr || "—"}
            </span>
          </div>
          <div className="mcell">
            <b>Status</b>
            <span style={{ color: e.STATUS[res.status].color, fontWeight: 600 }}>{e.STATUS[res.status].label}</span>
          </div>
        </div>
        <div className="field-l">Acceptance criteria</div>
        <div className="crit" style={{ whiteSpace: "pre-wrap" }}>
          {it.crit || "—"}
        </div>
        <ResultRow res={res} onSet={(s) => setItem(e.state.qcItem, s)} e={e} />
        <Gallery
          photos={evid.photos || []}
          scopeTag={scopeTag}
          onAdd={(files) => processAndAdd(files, (out) => { e.pushHistory(); if (inPile) e.Store.addPilePhotos(fid, t, P, out); else if (inMod) e.Store.addModulePhotos(fid, t, M, out); else e.Store.addPhotos(fid, t, e.state.qcItem, out); e.saveToStorage(); }, bump)}
          onDel={(id) => { if (confirm("Remove this photo? This can't be undone.")) { e.pushHistory(); if (inPile) e.Store.deletePilePhoto(fid, t, P, id); else if (inMod) e.Store.deleteModulePhoto(fid, t, M, id); else e.Store.deletePhoto(fid, t, e.state.qcItem, id); e.saveToStorage(); bump(); } }}
          onView={(id) => openLightbox((inPile ? e.getPile(fid, t, P) : inMod ? e.getModule(fid, t, M) : e.itemResult(fid, t, e.state.qcItem)).photos || [], id)}
        />
        <Thread
          comments={evid.comments || []}
          scopeTag={scopeTag}
          onPost={(text) => { e.pushHistory(); if (inPile) e.Store.addPileComment(fid, t, P, text); else if (inMod) e.Store.addModuleComment(fid, t, M, text); else e.Store.addComment(fid, t, e.state.qcItem, text); e.saveToStorage(); bump(); }}
          onDel={(id) => { if (inPile) e.Store.deletePileComment(fid, t, P, id); else if (inMod) e.Store.deleteModuleComment(fid, t, M, id); else e.Store.deleteComment(fid, t, e.state.qcItem, id); e.saveToStorage(); bump(); }}
        />
        <button className="export" onClick={() => e.exportReport()}>
          Export table report — {fid} T{t}
        </button>
      </IPanelShell>
    </div>
  );
}

// ---- shared: result row + filtered checklist wrapper ----
function ResultRow({ res, onSet, e }: { res: any; onSet: (s: string) => void; e: any }) {
  return (
    <>
      <div className="field-l">Result</div>
      <div className="resultrow">
        {["pass", "fail"].map((s) => {
          const on = res.status === s;
          const c = e.STATUS[s].color;
          return (
            <button key={s} className="rbtn" onClick={() => onSet(s)} style={{ background: on ? c : "var(--bg2)", color: on ? e.C.bg : c, border: `1px solid ${c}` }}>
              {s === "fail" ? "✕ Fail / raise NCR" : "✓ Pass"}
            </button>
          );
        })}
      </div>
    </>
  );
}

function FilteredChecklist({ itp, filt, getRes, qcItem, onPick, onSet, e }: any) {
  const shown = itp.map((it: any, i: number) => ({ it, i })).filter((x: any) => filt === "all" || getRes(x.i).status === filt);
  const visIdx = new Set(shown.map((x: any) => x.i));
  const filteredItp = itp.filter((_: any, i: number) => visIdx.has(i));
  // keep original indices by passing a getRes that maps; simplest: render only shown
  void filteredItp;
  const cats = [...new Set(shown.map((x: any) => x.it.cat))] as string[];
  if (!shown.length) {
    return (
      <div className="empty-state">
        <div className="es-ico">{filt === "fail" ? "✓" : "○"}</div>
        <div className="es-t">No {filt === "pass" ? "passed" : filt === "fail" ? "failed" : "open"} items</div>
        <div className="es-s">Switch filter to see other items.</div>
      </div>
    );
  }
  return (
    <>
      {cats.map((cat) => {
        const items = shown.filter((x: any) => x.it.cat === cat);
        const code = items[0].it.code;
        return (
          <div key={cat}>
            <div className="clcat">
              <span>{cat}</span>
              <span className="cc">{code}</span>
            </div>
            {items.map(({ it, i }: any) => {
              const res = getRes(i);
              const st = e.STATUS[res.status];
              return (
                <div className={`clrow ${i === qcItem ? "sel" : ""}`} key={i} onClick={() => onPick(i)} style={{ cursor: "pointer" }}>
                  <span className="clstat" style={{ background: st.color }}>
                    {st.glyph || ""}
                  </span>
                  <span className="clname">
                    {it.item}
                    <span className="clref">
                      BAL-{code}-{String(i + 1).padStart(3, "0")}
                    </span>
                  </span>
                  <span className="clbtns">
                    <button className={`clmini ${res.status === "pass" ? "pass" : ""}`} aria-label="Pass" onClick={(ev) => { ev.stopPropagation(); onSet(i, "pass"); }}>
                      ✓
                    </button>
                    <button className={`clmini ${res.status === "fail" ? "fail" : ""}`} aria-label="Fail" onClick={(ev) => { ev.stopPropagation(); onSet(i, "fail"); }}>
                      ✕
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
